import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { translations } from '../data/translations';
import { Sun, Moon, Globe } from 'lucide-react';
import { Button } from './ui/button';

export const Footer = () => {
  const { language, toggleLanguage } = useLanguage();
  const { theme, toggleTheme } = useTheme();

  const currentYear = new Date().getFullYear();

  return (
    <footer 
      className="py-12 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 theme-transition"
      data-testid="footer"
    >
      <div className="section-container">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
          {/* Logo & Description */}
          <div className="text-center md:text-left">
            <div className="flex items-center gap-2 justify-center md:justify-start mb-3">
              {/* A icon */}
              <img 
                src="https://customer-assets.emergentagent.com/job_platform-control-9/artifacts/v13sl9x3_Ads%C4%B1z%20tasar%C4%B1m%20%2815%29.png"
                alt="AgrosTech"
                className="w-8 h-8 object-contain dark:drop-shadow-[0_0_6px_rgba(255,255,255,0.2)]"
              />
              {/* Text */}
              <span className="text-lg font-bold tracking-tight">
                <span className="text-slate-900 dark:text-white">Agros</span>
                <span className="text-indigo-600 dark:text-indigo-400">Tech</span>
              </span>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-400 max-w-xs mx-auto md:mx-0">
              {translations.footer.description[language]}
            </p>
          </div>

          {/* Controls */}
          <div className="flex items-center justify-center gap-4">
            {/* Theme Toggle */}
            <div className="flex items-center gap-2 bg-slate-100 dark:bg-slate-800 rounded-full p-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleTheme}
                className={`rounded-full px-3 py-1 transition-all ${
                  theme === 'light' 
                    ? 'bg-white dark:bg-slate-700 shadow text-slate-900 dark:text-white' 
                    : 'text-slate-500 dark:text-slate-400'
                }`}
                data-testid="theme-light-button"
              >
                <Sun className="w-4 h-4 mr-1" strokeWidth={1.5} />
                <span className="text-xs">{translations.footer.light[language]}</span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleTheme}
                className={`rounded-full px-3 py-1 transition-all ${
                  theme === 'dark' 
                    ? 'bg-white dark:bg-slate-700 shadow text-slate-900 dark:text-white' 
                    : 'text-slate-500 dark:text-slate-400'
                }`}
                data-testid="theme-dark-button"
              >
                <Moon className="w-4 h-4 mr-1" strokeWidth={1.5} />
                <span className="text-xs">{translations.footer.dark[language]}</span>
              </Button>
            </div>

            {/* Language Toggle */}
            <Button
              variant="outline"
              size="sm"
              onClick={toggleLanguage}
              className="rounded-full flex items-center gap-2 border-slate-200 dark:border-slate-700"
              data-testid="footer-language-toggle"
            >
              <Globe className="w-4 h-4" strokeWidth={1.5} />
              <span className="text-xs font-medium uppercase">{language}</span>
            </Button>
          </div>

          {/* Contact & Copyright */}
          <div className="text-center md:text-right">
            <a 
              href="mailto:info@agrostech.com"
              className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline mb-2 block"
              data-testid="footer-email"
            >
              info@agrostech.com
            </a>
            <p className="text-xs text-slate-500 dark:text-slate-500">
              © {currentYear} AgrosTech. {translations.footer.rights[language]}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};
